import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { EmailRequestDto, MandatoryAcceptances } from '../models/dto/email-request-dto';
import { LandingpageConfigurationResponseDto } from '../models/dto/landingpage-configuration-response-dto';
import { LandingpageOrganisationResponseDto } from '../models/dto/landingpage-organisation-response-dto';
import { VerifyEmailRequestDto } from '../models/dto/verify-email-request-dto';
import { OrderRequestDto } from '../models/dto/order-request-dto';
import { AuthenticationStateService } from './state/authentication-state.service';
import { bypassAllErrorHandling, bypassClientErrorHandling } from './errors/error-bypass';

/**
 * BackendService is a service class that provides methods for interacting with the backend API.
 */
@Injectable({
  providedIn: 'root',
})
export class BackendService {
  private http = inject(HttpClient);
  private authenticationStateService = inject(AuthenticationStateService);

  /**
   * submitPersonalAccessToken is a method that sends a personal access token to the backend API.
   * @param personalAccessToken - External case identifier.
   */
  submitPersonalAccessToken(personalAccessToken: string) {
    return this.http.get<LandingpageOrganisationResponseDto>(
      `${environment.apiURL}/dzbank/landingpage/${personalAccessToken}/organisation`,
      { context: bypassClientErrorHandling() },
    );
  }

  getPublicLandingpageOrganisation(personalAccessToken: string) {
    return this.http.get<LandingpageOrganisationResponseDto>(
      `${environment.apiURL}/dzbank/landingpage/${personalAccessToken}/organisation/public`,
      { context: bypassClientErrorHandling() },
    );
  }

  /**
   * submitAuthenticationToken is a method that sends an authentication token to the backend API.
   * @param personalAccessToken - External case identifier.
   * @param authToken - The authentication token - this corresponds to the anonymised IBAN.
   */
  submitAuthenticationToken(authToken: string) {
    const personalAccessToken = this.authenticationStateService.personalAccessToken()!; // existence of personalAccessToken should be verified by canActivate guard
    const params = new HttpParams().append('password', authToken);
    return this.http.get<LandingpageConfigurationResponseDto>(
      `${environment.apiURL}/dzbank/landingpage/${personalAccessToken}`,
      { params, context: bypassAllErrorHandling() },
    );
  }

  /**
   *Sends the form values of the overview view to core backend.
   *
   * @param email the customer's e-mail. A verify code will be sent to this address
   * @param acceptances the boolean values of the mandatory checkboxes on the overview page
   */
  submitEmailAndAcceptedFields(email: string, acceptances: MandatoryAcceptances) {
    const personalAccessToken = this.authenticationStateService.personalAccessToken()!; // existence of personalAccessToken should be verified by canActivate guard
    const password = this.authenticationStateService.password()!; // existence of password should be verified by canActivate guard

    const emailRequestDto: EmailRequestDto = {
      recipientEmailAddress: email,
      lastFourIbanDigits: password,
      ...acceptances,
    };
    return this.http.post<void>(
      `${environment.apiURL}/dzbank/landingpage/${personalAccessToken}/mail`,
      emailRequestDto,
      { context: bypassClientErrorHandling() },
    );
  }

  verifyEmailCode(emailVerificationCode: string) {
    const personalAccessToken = this.authenticationStateService.personalAccessToken()!; // existence of personalAccessToken should be verified by canActivate guard
    const password = this.authenticationStateService.password()!; // existence of password should be verified by canActivate guard

    const verifyEmailRequestDto: VerifyEmailRequestDto = {
      lastFourIbanDigits: password,
      verificationCode: emailVerificationCode,
    };
    return this.http.post<void>(
      `${environment.apiURL}/dzbank/landingpage/${personalAccessToken}/verify-mail-code`,
      verifyEmailRequestDto,
      { context: bypassClientErrorHandling() },
    );
  }

  /**
   * Sends a POST Request to the backend which triggers the completion of the case
   * and the order of the creditcard
   */
  orderCreditCard() {
    const personalAccessToken = this.authenticationStateService.personalAccessToken()!; // existence of personalAccessToken should be verified by canActivate guard
    const password = this.authenticationStateService.password()!; // existence of password should be verified by canActivate guard
    const orderRequestDto: OrderRequestDto = {
      lastFourIbanDigits: password,
    };
    return this.http.post(`${environment.apiURL}/dzbank/landingpage/${personalAccessToken}/order`, orderRequestDto, {
      context: bypassClientErrorHandling(),
    });
  }
}
